import { useState, useEffect } from 'react';
import { getAxiosInstance } from '~/common/react/utils/networkUtil';
import { API_URL_ADMIN } from '~/config/config';

export interface AuthImageOptions {
  /**
   * OSS-style resize param.
   * e.g. "image/resize,w_300" | "image/resize,w_hd"
   */
  ossProcess?: string;
  /**
   * Stamp metadata onto the image (backend renders + caches as __stamp file).
   * Forces w_hd resize on backend.
   */
  stamp?: boolean;
}

/**
 * Hook to fetch an authenticated image and return a blob object URL.
 * The backend download endpoint requires a JWT token which browsers
 * do not send automatically via <img src=...>. This hook fetches
 * the image via axios (which attaches the Authorization header) and
 * converts it to a local blob URL that can be used in <img src=...>.
 *
 * Supports optional OSS-style resize and stamp params:
 *   ossProcess="image/resize,w_300"  → thumbnail
 *   stamp=true                       → HD resize + metadata stamp (cached as __stamp)
 *
 * @param uuid    - UUID of the FreePhoto (falsy = no-op)
 * @param options - Optional resize / stamp options
 */
export const useAuthImageUrl = (uuid?: string, options?: AuthImageOptions) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Build a stable cache key so the effect re-runs when options change
  const cacheKey = `${uuid}|${options?.ossProcess ?? ''}|${options?.stamp ?? false}`;

  useEffect(() => {
    if (!uuid) {
      setBlobUrl(null);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    setLoading(true);
    setError(false);
    setBlobUrl(null);

    // Build query string
    const params: Record<string, string> = {};
    if (options?.ossProcess) params['x-oss-process'] = options.ossProcess;
    if (options?.stamp) params['stamp'] = 'true';
    const qs = Object.keys(params).length
      ? '?' + new URLSearchParams(params).toString()
      : '';

    getAxiosInstance()
      .get(`/api/geocollect/restore/download-free-photo-file/${uuid}${qs}`, {
        responseType: 'blob',
        baseURL: API_URL_ADMIN,
      })
      .then((response) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  return { blobUrl, loading, error };
};